import { Link } from "react-router-dom";
import { Apple, Smartphone, Globe, ArrowRight, Heart, Share, Plus } from "lucide-react";
import { BrandLockup } from "@/components/BrandMark";
import { useSEO } from "@/lib/seo";

export default function Download() {
  useSEO({
    title: "Download SonSpeak — iPhone, iPad, Android, web",
    description:
      "Install SonSpeak on any device — iPhone, iPad, Android phone or tablet, or any web browser. Works offline. In early access.",
    path: "/download",
  });

  return (
    <div className="min-h-screen dotted-bg">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <BrandLockup size={180} />
        <Link to="/register" className="pill-btn primary"><ArrowRight size={14} /> Sign up</Link>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-12">
        <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3">Download</div>
        <h1 className="display text-4xl sm:text-6xl font-extrabold leading-tight mb-4">
          Get SonSpeak on any device.
        </h1>
        <p className="text-lg text-[color:var(--ink-soft)] mb-10">
          Works offline once installed. No card during launch. One account works across every device you sign in on.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <div className="brutal-card bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <Apple size={28} />
              <h2 className="display text-2xl font-extrabold">iPhone &amp; iPad</h2>
            </div>
            <ol className="space-y-2 text-sm leading-relaxed mb-4">
              <li><strong>1.</strong> Open <strong>sonspeak.com</strong> in Safari.</li>
              <li><strong>2.</strong> Tap <Share size={14} className="inline -mt-0.5" /> the Share icon at the bottom.</li>
              <li><strong>3.</strong> Scroll and tap <strong>"Add to Home Screen"</strong> <Plus size={14} className="inline -mt-0.5" /></li>
              <li><strong>4.</strong> Tap <strong>"Add"</strong>. SonSpeak now lives on your home screen like any app.</li>
            </ol>
            <p className="text-xs text-[color:var(--ink-soft)]">A native App Store release is in review. The web install above gives you the same experience offline today.</p>
          </div>

          <div className="brutal-card bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone size={28} />
              <h2 className="display text-2xl font-extrabold">Android</h2>
            </div>
            <ol className="space-y-2 text-sm leading-relaxed mb-4">
              <li><strong>1.</strong> Open <strong>sonspeak.com</strong> in Chrome.</li>
              <li><strong>2.</strong> Tap the <strong>⋮</strong> menu (top right).</li>
              <li><strong>3.</strong> Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
              <li><strong>4.</strong> Tap <strong>"Install"</strong>. Done.</li>
            </ol>
            <p className="text-xs text-[color:var(--ink-soft)]">Google Play release coming. Installed PWA gets full offline + home-screen icon.</p>
          </div>

          <div className="brutal-card bg-white p-5 sm:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Globe size={28} />
              <h2 className="display text-2xl font-extrabold">Any computer</h2>
            </div>
            <p className="text-sm leading-relaxed mb-2">
              Just open <strong>sonspeak.com</strong> in Chrome, Safari, Edge, or Firefox. Sign in with the same account you use on your phone — boards sync.
            </p>
            <Link to="/register" className="pill-btn primary mt-2">
              <Heart size={14} fill="currentColor" /> Create your account
            </Link>
          </div>
        </div>

        <section className="brutal-card bg-[color:var(--cream-deep)] p-5 text-sm">
          <strong>Privacy:</strong> SonSpeak works fully offline once installed. Your child's tiles, photos, and voice recordings live on your device. Anything you choose to share publicly (a family story, a clinician profile) is opt-in and reviewable from your settings.
        </section>
      </main>
    </div>
  );
}
